// Native-shell bridge protocol — the postMessage contract shared by the React Native
// shell (apps/mobile) and the in-WebView shim (apps/web).
//
// IMPORTANT: this module must stay DEPENDENCY-FREE (no React, tiptap, react-email, yjs).
// It is imported by React Native's Metro bundler via a leaf subpath
// (`@naumu/shared/native/bridge`), so anything it transitively imports ends up in the
// mobile bundle. Keep it pure TypeScript.

import type {
	AgentTranscriptDesktopState,
	AgentTranscriptWatchFolder,
} from '../types/agent-transcripts';
import type { ExternalAgentCompactFailure, ExternalAgentSessionContext } from '../types/external-agent';
import type {
	ExternalAgentEngine,
	ExternalAgentEngineState,
	ExternalAgentRunSettings,
} from '../types/external-agent-engines';

/** Bumped when the wire protocol changes in a non-backward-compatible way. */
export const NATIVE_BRIDGE_VERSION = 1;

/**
 * Global the native shell injects into the WebView (before content loads) so the web app
 * can detect it is running inside the native wrapper: `window.__NAUMU_NATIVE__ === true`.
 */
export const NATIVE_FLAG = '__NAUMU_NATIVE__';

export type OAuthProvider = 'google' | 'microsoft' | 'linear' | 'jira';

/** How a native (system-browser) OAuth sign-in started by `OAUTH_START` ended. */
export type NativeOAuthResult = 'authenticated' | 'cancelled' | 'failed';

/**
 * Custom URL schemes a coding agent registers with the OS, which the app may ask
 * the native shell to hand to the system (`OPEN_AGENT_LINK`).
 *
 * `OPEN_EXTERNAL` deliberately accepts http/https ONLY, because page content must
 * never be able to hand an arbitrary scheme to the OS. These schemes are the
 * narrow, audited exception: each one opens a local coding agent with a prompt
 * prefilled, and none of them execute anything on arrival. Keep this list closed
 * - adding to it widens what a compromised renderer could launch.
 *
 * Note `vscode:` is NOT here. Its handler accepts URLs that drive the editor
 * beyond prefilling a prompt, so it isn't safe to expose behind a blanket
 * scheme check.
 */
export const AGENT_LINK_SCHEMES: readonly string[] = ['claude-cli:', 'codex:', 'cursor:'];

/**
 * Which native shell the web app is running inside. `ios` / `android` are the
 * React Native WebView wrapper (apps/mobile); `desktop` is the Electron wrapper
 * (apps/desktop). Note this is the SHELL kind — the desktop shell separately
 * reports its OS via `NaumuDesktopBridge.platform` (see below).
 */
export type NativePlatform = 'ios' | 'android' | 'desktop';

/** OS the Electron desktop shell runs on (mirrors Electron's `process.platform`). */
export type DesktopPlatform = 'darwin' | 'win32' | 'linux';

/** Name of the global the Electron desktop shell injects via `contextBridge`. */
export const DESKTOP_BRIDGE_KEY = 'naumuDesktop';

/**
 * Bridge object the Electron desktop shell injects on `window` (before content
 * loads) for the web→native direction. Present ONLY inside the desktop shell;
 * `undefined` in a normal browser and in the React Native WebView (which uses
 * `window.ReactNativeWebView` instead). The desktop shell ALSO sets
 * `window.__NAUMU_NATIVE__ === true`, so the existing `isNativeShell()`
 * machinery keeps working unchanged.
 */
export interface NaumuDesktopBridge {
	/** OS the shell runs on. */
	platform: DesktopPlatform;
	/** Version of the desktop shell (Electron app version). */
	appVersion: string;
	/**
	 * Window corner radius (CSS px) the page must clip itself to. Non-zero only
	 * for transparent windows (macOS 26 Liquid Glass), where the OS doesn't clip
	 * web content to the rounded window shape; 0 when the OS clips natively.
	 * Optional: older shell builds don't report it.
	 */
	windowRadius?: number;
	/**
	 * True when this shell can grant system-audio loopback to `getDisplayMedia`
	 * (macOS via CoreAudio tap, Windows via WASAPI loopback), letting the web
	 * recorder capture meeting audio alongside the microphone. Optional: older
	 * shell builds don't report it, which reads as unavailable.
	 */
	systemAudioCapture?: boolean;
	/** Send a serialized `WebToNativeMessage` (JSON string) out to the shell. */
	postMessage: (json: string) => void;
}

export interface SharedFile {
	/** Local file URI inside the app sandbox (or content:// on Android). */
	uri: string;
	mimeType?: string;
	fileName?: string;
	size?: number;
}

/** Content handed to the web app when the user shares into Naumu via the OS share sheet. */
export interface SharePayload {
	text?: string;
	url?: string;
	files?: SharedFile[];
}

/** Meeting recorder lifecycle as the page reports it in RECORDING_STATUS. */
export type MeetingRecordingState =
	| 'idle'
	| 'preparing'
	| 'recording'
	| 'stopped'
	| 'uploading'
	| 'sent'
	| 'error';

/** Where a meeting recording was started from. */
export type MeetingStartSource = 'pill' | 'tray' | 'auto';

/** A space the viewer can file a meeting note into (pill space switcher, tray submenu). */
export interface MeetingSpace {
	id: string;
	name: string;
	slug: string;
	iconUrl?: string;
}

/** A key combination, mirroring the web app's `KeyBinding` shape. */
export interface NativeMenuShortcut {
	key: string;
	modifiers?: {
		meta?: boolean;
		shift?: boolean;
		alt?: boolean;
		ctrl?: boolean;
	};
}

/**
 * One in-app command projected into the desktop shell's native menu bar.
 *
 * Only a SUBSET of the web app's commands can cross this boundary. A native menu accelerator
 * is app-global — it fires even while a text field has focus, and it has no notion of the
 * web app's `CommandContext`. The web side is responsible for sending only bindings that are
 * modifier-bearing (so no character becomes untypeable) and context-free.
 */
export interface NativeMenuCommand {
	/** The web app's `CommandId` — echoed back verbatim in `RUN_COMMAND`. */
	id: string;
	title: string;
	shortcut: NativeMenuShortcut;
}

/**
 * Messages the WEB app sends OUT to the native shell, via
 * `window.ReactNativeWebView.postMessage(JSON.stringify(msg))`.
 */
/**
 * Bridge features a web build supports, declared on READY. Lets a shell tell a web build that
 * predates a message apart from one that will send it: without `content-ready` the desktop
 * shell lifts its boot splash on READY instead of waiting for CONTENT_READY.
 */
export type WebShellCapability =
	| 'content-ready'
	/**
	 * This build names the signed-in account (`userId`) on every `AGENT_TRANSCRIPTS_*` message.
	 * The desktop shell then shows this document no session-sync state until it has done so.
	 */
	| 'agent-transcripts-user';

export type WebToNativeMessage =
	| { type: 'READY'; shellVersion?: number; capabilities?: WebShellCapability[] }
	/**
	 * The first real UI is painted (a shell past its full-viewport skeleton). Posted once per
	 * document by the desktop-only `useNativeContentReady`; the desktop shell crossfades its
	 * boot splash away on it. Distinct from READY, which fires when the bridge provider
	 * mounts, long before the skeletons resolve. The mobile shell ignores it.
	 */
	| { type: 'CONTENT_READY' }
	| { type: 'OAUTH_START'; provider: OAuthProvider; initUrl?: string }
	| { type: 'AUTH_CHANGED'; authenticated: boolean }
	| { type: 'REQUEST_PUSH_REGISTRATION' }
	| { type: 'LOGOUT' }
	| { type: 'SET_BADGE'; count: number }
	| { type: 'OPEN_EXTERNAL'; url: string }
	| { type: 'OPEN_AGENT_LINK'; url: string }
	| { type: 'HAPTIC'; style?: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' }
	| { type: 'SET_DESKTOP_NOTIFICATIONS'; enabled: boolean }
	| { type: 'SET_LAUNCH_AT_LOGIN'; enabled: boolean }
	| { type: 'REQUEST_DESKTOP_STATE' }
	| { type: 'INSTALL_UPDATE' }
	| { type: 'SET_MENU_COMMANDS'; commands: NativeMenuCommand[] }
	| { type: 'RECORDING_STATE'; recording: boolean }
	/**
	 * Meeting recorder lifecycle (desktop-only, see .design/MEETING_DETECT_PILL.md). Replaces
	 * the boolean RECORDING_STATE for meeting recordings: drives the shell's pill window, tray
	 * items and the mic-monitor self-suppression. `elapsedMs` is the recording clock.
	 */
	| {
			type: 'RECORDING_STATUS';
			state: MeetingRecordingState;
			elapsedMs: number;
			graphId?: string;
			threadId?: string;
			systemAudio: boolean;
			app?: string;
			error?: string;
	  }
	/** The meeting thread has its transcript AND the agent's reply: the shell shows the "ready" pill. */
	| { type: 'MEETING_NOTE_READY'; graphId: string; threadId: string; path: string }
	/**
	 * Spaces the viewer may file a meeting note into (answer to GET_MEETING_SPACES, also sent
	 * when the list or the active space changes). `activeGraphId` is the space the app has open
	 * (one of `spaces`, else absent): the pill pre-selects it unless a Default space is set (D2).
	 */
	| { type: 'MEETING_SPACES'; spaces: MeetingSpace[]; activeGraphId?: string }
	/** Desktop meeting-detection settings edited in the web settings UI; the shell persists and echoes MEETING_SETTINGS. */
	| {
			type: 'SET_MEETING_SETTINGS';
			detection: boolean;
			autoRecord: boolean;
			excludedApps: string[];
			defaultGraphId?: string;
	  }
	/**
	 * Open the OS settings pane where the user grants system-audio capture
	 * (macOS "System Audio Recording Only"). Sent from the recorder's recovery
	 * toast when the loopback track comes up dead. No-op on other platforms.
	 */
	| { type: 'OPEN_CAPTURE_SETTINGS' }
	/**
	 * External agent (Claude Code / Codex on this Mac). `REQUEST_EXTERNAL_AGENT_STATE`
	 * asks for the per-graph setup state; `EXTERNAL_AGENT_PICK_FOLDER` opens the
	 * native folder picker and stores the choice for `graphId`. Both are answered
	 * with `EXTERNAL_AGENT_STATE`. Desktop-only; the mobile shell ignores them.
	 */
	| { type: 'REQUEST_EXTERNAL_AGENT_STATE'; graphId: string }
	| { type: 'EXTERNAL_AGENT_PICK_FOLDER'; graphId: string }
	/** Open the guidance for installing / signing in to the engine (opens a terminal or docs). */
	| { type: 'EXTERNAL_AGENT_OPEN_ENGINE_SETUP'; engine: ExternalAgentEngine }
	/**
	 * Compact the viewer's engine session for `threadId` - the session of
	 * `settings.engine` (Claude Code's own `/compact`, Codex's native
	 * `thread/compact/start`), run headlessly on this Mac against the stored
	 * session with the same run settings so the cached prompt prefix holds. Answered with
	 * `EXTERNAL_AGENT_COMPACTED`; the new reading also reaches the backend, which
	 * pushes it to the composer ring.
	 */
	| { type: 'EXTERNAL_AGENT_COMPACT'; graphId: string; threadId: string; settings: ExternalAgentRunSettings }
	/**
	 * Desktop huddle window (.design/IN_APP_HUDDLES.md D10, phase 1e). MAIN window only: open
	 * (or focus) the separate call window on `/huddle/:huddleId`. A different huddle already in
	 * a call gets the native leave-confirm before the switch. `via` (set by the ring) is put on
	 * the call window URL so its lobby reports how the join happened.
	 */
	| { type: 'HUDDLE_OPEN'; huddleId: string; mode?: HuddleWindowMode; via?: HuddleOpenVia }
	/**
	 * Either window: ask for the current huddle-window state. The main window gets
	 * HUDDLE_WINDOW_STATE, the huddle window HUDDLE_MODE.
	 */
	| { type: 'HUDDLE_REQUEST_STATE' }
	/** HUDDLE window only: the call state its page is in (drives close/quit confirmation). */
	| { type: 'HUDDLE_STATE'; huddleId: string; state: HuddleWindowCallState }
	/** HUDDLE window only: switch between the compact PiP and the expanded stage. */
	| { type: 'HUDDLE_SET_MODE'; mode: HuddleWindowMode }
	/** HUDDLE window only: close the window (the page has already left the call). */
	| { type: 'HUDDLE_CLOSE' }
	/** HUDDLE window only: show an app path (a link in the chat drawer) in the main window. */
	| { type: 'HUDDLE_OPEN_IN_MAIN'; path: string }
	/**
	 * Agent session sync (.design/SESSION_SYNC.md). Desktop-only; the mobile shell ignores
	 * them. Every message is answered with `AGENT_TRANSCRIPTS_STATE`.
	 * `AGENT_TRANSCRIPTS_PICK_FOLDER` opens the native folder picker and links the choice to
	 * `graphId`; `AGENT_TRANSCRIPTS_SET_FOLDERS` replaces the whole watch-folder list (unlink
	 * = omit the row, move to another space = change its `graphId`).
	 *
	 * `userId` on all four: the account the page is signed in as; the shell refuses to show or
	 * change another account's state (it answers as signed out and re-resolves who is signed
	 * in). Optional only so a web build that predates it keeps working against a newer shell.
	 */
	| { type: 'REQUEST_AGENT_TRANSCRIPTS_STATE'; userId?: string }
	| { type: 'AGENT_TRANSCRIPTS_PICK_FOLDER'; graphId: string; userId?: string }
	| {
			type: 'AGENT_TRANSCRIPTS_SET_FOLDERS';
			folders: Array<Pick<AgentTranscriptWatchFolder, 'path' | 'graphId'>>;
			userId?: string;
	  }
	| { type: 'AGENT_TRANSCRIPTS_DISMISS_ONBOARDING'; userId?: string };

/** Desktop huddle window layout: `compact` = self tile + controls, `expanded` = stage + chat. */
export type HuddleWindowMode = 'compact' | 'expanded';

/** How a huddle screen was opened when that matters for the join's `via`: the ring, or the short link `/h/{code}` (R7). */
export type HuddleOpenVia = 'ring' | 'link';

/**
 * Call state the huddle window's page reports. `connecting` and `joined` count as "in a call":
 * closing, quitting or switching huddles then asks before leaving.
 */
export type HuddleWindowCallState = 'lobby' | 'connecting' | 'joined' | 'left' | 'ended';

/**
 * Messages the NATIVE shell sends IN to the web app, via `webviewRef.postMessage()` (delivered
 * to a `window` `message` listener) or `injectJavaScript`.
 */
export type NativeToWebMessage =
	| { type: 'NAVIGATE'; path: string }
	| { type: 'SHARE_CONTENT'; payload: SharePayload }
	| { type: 'PUSH_REGISTERED'; token: string; platform: NativePlatform }
	| { type: 'SESSION_SYNCED'; authenticated: boolean }
	/**
	 * The native sign-in started by `OAUTH_START` ended. On anything but `authenticated`
	 * the web resets its "Redirecting…" button; on success the shell reloads to boot anyway.
	 * Older shells never send it.
	 */
	| { type: 'OAUTH_FINISHED'; provider: OAuthProvider; result: NativeOAuthResult }
	| { type: 'DESKTOP_STATE'; notificationsEnabled: boolean; launchAtLogin: boolean }
	| { type: 'UPDATE_READY'; version: string }
	/**
	 * Withdraw a previously sent UPDATE_READY: the huddle window opened, and the shell will not
	 * install while a call can be live (IN_APP_HUDDLES.md 13.3). UPDATE_READY follows again once
	 * the huddle window closes.
	 */
	| { type: 'UPDATE_HIDDEN' }
	| { type: 'RUN_COMMAND'; commandId: string }
	| { type: 'BACK' }
	/**
	 * Legacy (pre-pill desktop shells): the mic monitor observed another process
	 * opening (active) or releasing (inactive) the microphone. Shells with the
	 * meeting pill keep detection in the main process and never send it; the web
	 * app ignores it.
	 */
	| { type: 'MIC_ACTIVITY'; active: boolean; app?: string }
	/**
	 * The user pressed Start on the shell's meeting pill (or the tray's "Start meeting
	 * note") - an explicit instruction to start a meeting recording (mic + system audio)
	 * filed into `graphId`. `app` is the detected meeting app ("Manual" from the tray).
	 */
	| { type: 'START_MEETING_RECORDING'; app?: string; graphId: string; source: MeetingStartSource }
	/** Stop the running meeting recording (pill/tray Stop, or the meeting app released the mic). */
	| { type: 'STOP_MEETING_RECORDING' }
	/** Ask the page for the current MEETING_SPACES list. */
	| { type: 'GET_MEETING_SPACES' }
	/** Persisted desktop meeting settings, sent on page load and after every SET_MEETING_SETTINGS. */
	| {
			type: 'MEETING_SETTINGS';
			detection: boolean;
			autoRecord: boolean;
			excludedApps: string[];
			defaultGraphId?: string;
			/** Every meeting app the shell has ever seen take the mic, for the exclusion UI. */
			detectedApps: string[];
	  }
	/**
	 * Per-graph external-agent setup state on this desktop. `folder` null =
	 * not set up for that graph yet. Engine fields mirror `ExternalAgentPresence`.
	 * `engine` is CLAUDE's state, kept for web builds that predate `engines`;
	 * `engines` carries every engine this desktop build drives. A desktop that
	 * sends no `engines` (or no `codex` key in it) predates the Codex engine.
	 */
	| {
			type: 'EXTERNAL_AGENT_STATE';
			graphId: string;
			folder: string | null;
			engine: { installed: boolean; loggedIn: boolean; version?: string };
			engines?: Partial<Record<ExternalAgentEngine, ExternalAgentEngineState>>;
	  }
	/** Outcome of `EXTERNAL_AGENT_COMPACT` for `threadId`; `context` is the post-compaction reading. */
	| {
			type: 'EXTERNAL_AGENT_COMPACTED';
			threadId: string;
			/** Absent from a desktop that predates Codex = `claude`. */
			engine?: ExternalAgentEngine;
			ok: true;
			context: ExternalAgentSessionContext;
	  }
	| {
			type: 'EXTERNAL_AGENT_COMPACTED';
			threadId: string;
			engine?: ExternalAgentEngine;
			ok: false;
			reason: ExternalAgentCompactFailure;
			detail?: string;
	  }
	/**
	 * To the MAIN window: which huddle the desktop call window hosts (`huddleId` null = no
	 * window). Sent on every change and on READY, so receiving it at all means this shell
	 * supports the huddle window.
	 */
	| {
			type: 'HUDDLE_WINDOW_STATE';
			huddleId: string | null;
			state: HuddleWindowCallState | null;
			mode: HuddleWindowMode | null;
	  }
	/** To the HUDDLE window: the layout mode now applied (answer to HUDDLE_SET_MODE, and on READY). */
	| { type: 'HUDDLE_MODE'; mode: HuddleWindowMode }
	/**
	 * To the HUDDLE window: the user confirmed leaving (close, quit, or switch to another huddle).
	 * Leave the call (POST /leave + disconnect), then report HUDDLE_STATE `left`. The shell
	 * closes or reloads the window after that, or after a short timeout.
	 */
	| { type: 'HUDDLE_LEAVE_REQUEST'; reason: 'close' | 'quit' | 'switch' }
	/**
	 * Agent session sync state (answer to every `AGENT_TRANSCRIPTS_*` message, and pushed
	 * whenever a folder's counts change). Receiving it at all means this shell can sync
	 * transcripts; the web app shows the onboarding dialog when a CLI is detected and
	 * `onboardingDismissed` is false (.design/SESSION_SYNC.md D4).
	 */
	| ({ type: 'AGENT_TRANSCRIPTS_STATE' } & AgentTranscriptDesktopState);

export type WebToNativeType = WebToNativeMessage['type'];
export type NativeToWebType = NativeToWebMessage['type'];

/** Serialize any bridge message to the single string the WebView bridge transports. */
export function serializeMessage(msg: WebToNativeMessage | NativeToWebMessage): string {
	return JSON.stringify(msg);
}

function parse(raw: string): Record<string, unknown> | null {
	try {
		const value = JSON.parse(raw);
		if (value && typeof value === 'object' && typeof (value as { type?: unknown }).type === 'string') {
			return value as Record<string, unknown>;
		}
		return null;
	} catch {
		return null;
	}
}

/** Parse a message that the web app sent to native. Returns null on malformed input. */
export function parseWebToNative(raw: string): WebToNativeMessage | null {
	return parse(raw) as WebToNativeMessage | null;
}

/** Parse a message that native sent to the web app. Returns null on malformed input. */
export function parseNativeToWeb(raw: string): NativeToWebMessage | null {
	return parse(raw) as NativeToWebMessage | null;
}
